import { CATEGORY, EVENT_TYPE, displayEventType } from '../constants/eventTypes.js';

/**
 * Customer classification — what it is and why this file exists.
 *
 * Per the prototype delivery call (Lex + Sebastian, Aug 12 recording, ~38:09):
 * "Customer no es un tipo de evento, porque Customer puede ser cualquiera de
 * estos eventos que estén relacionados con un Customer." Customer is a TAG
 * that can land on ANY event type (Governance, Identity, Voice, Qualification,
 * HTL Handoff, Question…), not a wire type of its own. Lex put it inside the
 * "Event Type" dropdown as one pseudo-option purely for UI pragmatism ("no
 * tengo otra variable adicional... pues lo coloqué aquí en eventos") — there is
 * no separate "Customer" filter column in the prototype, and clicking it is
 * expected to surface a heterogeneous mix of types that all share the tag.
 *
 * How this file resolves it TODAY (front-only, no backend contract yet):
 * {@link resolveIsCustomer} runs a priority cascade — explicit flag first,
 * falling through to weaker and weaker signals, down to a text heuristic over
 * title/description/situation built from the exact customer-facing examples
 * Lex demoed (webchat cancellation intent, vulnerable-customer voice calls,
 * client handoff/continuation). This is a deliberate stand-in: the interim
 * fan-out (GS `my-work/*` + HITL `hitl/list`, pending CT-4867's unified
 * endpoint) never sends a customer flag today, so without the heuristic the
 * tag would only ever show on `client_continuation`/`client_handoff` rows —
 * missing every Governance/Identity/Voice/Qualification example from the demo.
 *
 * What backend should own for future work (once available, it wins
 * immediately — see the cascade order in {@link resolveIsCustomer}):
 * - A real `is_customer` (snake_case, GS convention) / `isCustomer` boolean on
 *   every row of the unified Work Queue list (CT-4867), set at the source
 *   (LangGraph `render.is_customer` for HITL rows already has a spot reserved
 *   here — the mapper reads it — so LangGraph attaching that field to the
 *   `render` blob is the smallest lift).
 * - Once that field is real and reliably populated, the text-heuristic branch
 *   in `resolveIsCustomer` (customerTextMatch / CUSTOMER_TEXT_SIGNALS) becomes
 *   dead weight that can be deleted — it exists ONLY because the flag doesn't
 *   exist yet, not because text-sniffing is the right long-term signal.
 *
 * @see mapApiEventToUi.js, mapHitlToEvent.js — the two mappers that feed this.
 */

/**
 * Pseudo-value for the Event Type filter — Customer is a tag/classification,
 * not a wire event type (aims-htl V1 / Lex delivery recap).
 */
export const TYPE_FILTER_CUSTOMER = 'customer';

/** Client-channel HITL types rolled into the Customer filter (not separate rows). */
export const CUSTOMER_WIRE_TYPES = new Set([
  EVENT_TYPE.CLIENT_CONTINUATION,
  EVENT_TYPE.CLIENT_HANDOFF,
]);

/** GE classes that prototype marks as customer-facing comms (webchat, voice, etc.). */
const CUSTOMER_GE_CLASSES = new Set(['GE-COMM']);

/** Substrings that indicate a customer-facing row when found in inbox copy. */
const CUSTOMER_TEXT_SIGNALS = [
  'customer',
  'webchat',
  'web chat',
  'live chat',
  'voice:',
  'voice assistant',
  'client handoff',
  'client continuation',
  'client plane',
  'cancellation intent',
  'vulnerable customer',
  'customerescalation',
  'client ·',
];

function customerTextMatch(...parts) {
  const haystack = parts.filter(Boolean).join(' ').toLowerCase();
  if (!haystack) return false;
  return CUSTOMER_TEXT_SIGNALS.some((signal) => haystack.includes(signal));
}

/**
 * Resolve whether a raw or mapped row is customer-facing.
 *
 * V1 front rules (prototype parity without unified list API):
 * - explicit `isCustomer` / `is_customer`
 * - LangGraph `render.is_customer`
 * - wire type `client_*`
 * - category CLIENT, team client, Client Plane destination
 * - GE-COMM on HITL continuations
 * - customer-facing copy in title / body (webchat, voice, etc.)
 */
export function resolveIsCustomer({
  type,
  isCustomer,
  is_customer,
  render,
  category,
  team,
  title,
  description,
  situation,
  refLabel,
  destination,
  geClassification,
  channel,
} = {}) {
  if (isCustomer === true || is_customer === true) return true;
  if (render?.is_customer === true || render?.isCustomer === true) return true;
  if (type && CUSTOMER_WIRE_TYPES.has(type)) return true;
  if (category === CATEGORY.CLIENT || category === 'CLIENT') return true;
  if (team === 'client') return true;
  if (typeof destination === 'string' && /client/i.test(destination)) return true;
  if (typeof refLabel === 'string' && /client/i.test(refLabel)) return true;

  const ge =
    geClassification ??
    render?.ge_classification ??
    render?.geClassification;
  if (ge && CUSTOMER_GE_CLASSES.has(String(ge).toUpperCase())) return true;

  if (
    customerTextMatch(
      title,
      description,
      situation,
      refLabel,
      destination,
      channel,
      render?.title,
      render?.summary,
    )
  ) {
    return true;
  }

  return false;
}

/** Classify a mapped inbox event (post-{@link mapApiEventToUi}). */
export function eventIsCustomer(event = {}) {
  if (!event || typeof event !== 'object') return false;
  const detail = event.detail ?? {};
  return resolveIsCustomer({
    type: event.type,
    isCustomer: event.isCustomer,
    is_customer: event.is_customer,
    render: detail.render ?? event.render,
    category: event.category,
    team: event.team,
    title: event.title,
    description: event.description,
    situation: event.situation,
    refLabel: event.refLabel,
    destination: event.destination,
    geClassification: detail.geClassification ?? detail.ge_classification,
    channel: detail.channel ?? detail.workflow,
  });
}

/**
 * Event Type multiselect match — union semantics.
 * `customer` matches any customer-classified row; other values match display type.
 */
export function matchesEventTypeFilter(event, selectedTypes = []) {
  if (!selectedTypes.length) return true;
  const display = displayEventType(event.type);
  const typeMatch = selectedTypes.some(
    (sel) => sel !== TYPE_FILTER_CUSTOMER && sel === display,
  );
  const customerMatch =
    selectedTypes.includes(TYPE_FILTER_CUSTOMER) && eventIsCustomer(event);
  return typeMatch || customerMatch;
}
